<template>
  <div class="entity-collections-page" :class="{ 'entity-collections-page-rtl': isRtl === true }" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div class="entity-collections-header">
      <div class="entity-collections-header-intro">
        <h2 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">{{ t('entities.collectionsTitle', 'Entity collections') }}</h2>
        <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">{{ t('entities.collectionsSubtitle', 'Create reusable collections by combining entities for reports and analysis.') }}</p>
      </div>
      <button class="btn" type="button" @click="openCreateModal">
        {{ t('entities.createCollectionButton', 'Create collection') }}
      </button>
    </div>

    <div class="entity-collections-layout">
      <div class="entity-collections-list-card bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-md shadow">
        <div class="entity-collections-list-toolbar">
          <label class="input input-bordered flex items-center entity-collections-search-input">
            <input
              v-model="searchValue"
              type="text"
              class="grow"
              maxlength="120"
              autocomplete="off"
              :placeholder="t('entities.collectionSearchPlaceholder', 'Search collections')"
            />
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="h-4 w-4 opacity-70">
              <path fill-rule="evenodd" d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0a3.5 3.5 0 0 1 7 0Z" clip-rule="evenodd" />
            </svg>
          </label>
        </div>
        <div v-if="isLoading === true" class="entity-collections-state">
          <span class="loading loading-spinner loading-lg text-indigo-600"></span>
        </div>
        <div v-else-if="collections.length === 0" class="entity-collections-state">
          {{ t('entities.collectionsEmpty', 'No collections yet. Create one to reuse a group of entities.') }}
        </div>
        <div v-else-if="filteredCollections.length === 0" class="entity-collections-state">
          {{ t('entities.searchCollectionsEmpty', 'No collections match your search.') }}
        </div>
        <div v-else class="entity-collections-list-scroll">
          <div class="entity-collections-list">
            <button
              v-for="collection in filteredCollections"
              :key="String(collection.collectionId)"
              type="button"
              class="entity-collections-list-item"
              :class="{ 'entity-collections-list-item-active': selectedCollectionId === String(collection.collectionId) }"
              @click="selectCollection(collection.collectionId)"
            >
              <div class="entity-collections-list-item-icon">
                <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" width="22" height="22" viewBox="0 0 24 24">
                  <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
                    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <path d="M8 13h8" />
                  </g>
                </svg>
              </div>
              <div class="entity-collections-list-item-copy">
                <div class="font-semibold text-gray-900 dark:text-gray-100">{{ collection.collectionName }}</div>
                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ getCollectionCountLabel(collection) }}</div>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div class="entity-collections-detail-card bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-md shadow">
        <div v-if="selectedCollection === null" class="entity-collections-state">
          {{ t('entities.collectionsSelectHint', 'Select a collection to view and manage its entities.') }}
        </div>
        <div v-else class="entity-collections-detail">
          <div class="entity-collections-detail-header">
            <div class="entity-collections-detail-icon">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" width="32" height="32" viewBox="0 0 24 24">
                <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
                  <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <path d="M8 13h8" />
                </g>
              </svg>
            </div>
            <div class="entity-collections-detail-heading">
              <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100">{{ selectedCollection.collectionName }}</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ getCollectionCountLabel(selectedCollection) }}</p>
            </div>
            <div class="entity-collections-detail-actions">
              <button type="button" class="btn btn-sm" @click="openEditModal">
                {{ t('entities.editCollectionButton', 'Edit collection') }}
              </button>
              <button type="button" class="btn btn-sm border-transparent bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-900" @click="openDeleteModal">
                {{ t('entities.deleteCollectionButton', 'Delete collection') }}
              </button>
            </div>
          </div>

          <div class="entity-collections-detail-grid">
            <div class="entity-collections-detail-field">
              <div class="entity-collections-detail-label">{{ t('entities.fields.collectionId', 'Collection ID') }}</div>
              <div class="entity-collections-detail-value entity-collections-detail-mono">{{ selectedCollection.collectionId }}</div>
            </div>
            <div class="entity-collections-detail-field">
              <div class="entity-collections-detail-label">{{ t('entities.fields.entityCount', 'Entities') }}</div>
              <div class="entity-collections-detail-value">{{ getCollectionEntityCount(selectedCollection) }}</div>
            </div>
          </div>

          <div class="entity-collections-members-section">
            <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('entities.collectionMembersTitle', 'Included entities') }}</h4>
            <div v-if="getCollectionMemberRows(selectedCollection).length === 0" class="text-sm text-gray-500 dark:text-gray-400 mt-3">
              {{ t('entities.collectionMembersEmpty', 'This collection does not include entities yet.') }}
            </div>
            <div v-else class="entity-collections-members-list">
              <div v-for="member in getCollectionMemberRows(selectedCollection)" :key="member.entityId" class="entity-collections-member-row">
                <div class="entity-collections-member-copy">
                  <div class="font-medium text-gray-900 dark:text-gray-100">{{ member.displayName }}</div>
                  <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ member.typeLabel }}</div>
                </div>
                <div class="entity-collections-member-id text-xs text-gray-500 dark:text-gray-400">{{ member.entityId }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AppModal :visible="collectionModalOpen === true" :title="collectionModalTitle" boxClass="max-w-2xl w-11/12" @close="closeCollectionModal">
      <div class="p-4" :class="{ 'text-right': isRtl === true }" :dir="isRtl === true ? 'rtl' : 'ltr'">
        <div class="form-control">
          <label class="label">
            <span class="label-text font-medium">{{ t('entities.collectionNameLabel', 'Collection name') }}</span>
          </label>
          <input
            v-model="collectionDraft.collectionName"
            type="text"
            maxlength="255"
            class="input input-bordered w-full"
            :placeholder="t('entities.collectionNamePlaceholder', 'Support teams')"
          />
        </div>
        <div class="form-control mt-4">
          <MultiSelect
            v-model="collectionDraft.entityIds"
            :options="entityOptions"
            :label="t('entities.collectionEntitiesLabel', 'Entities')"
            :placeholder="t('entities.collectionEntitiesPlaceholder', 'Select entities')"
            id-field="entityId"
            label-field="displayName"
            :is-loading="false"
            :force-direction="isRtl === true ? 'rtl' : 'ltr'"
          />
        </div>
      </div>
      <template #actions>
        <div class="entity-collections-modal-actions" :class="{ 'entity-collections-modal-actions-rtl': isRtl === true }">
          <button type="button" class="btn" :disabled="isSaving === true" @click="closeCollectionModal">
            {{ t('entities.cancelButton', 'Cancel') }}
          </button>
          <button type="button" class="btn" :disabled="isSaving === true" @click="saveCollection">
            <span v-if="isSaving === true" class="loading loading-spinner loading-xs entity-collections-save-spinner"></span>
            {{ collectionModalActionLabel }}
          </button>
        </div>
      </template>
    </AppModal>

    <AppModal :visible="deleteModalOpen === true" :title="t('entities.deleteCollectionModalTitle', 'Delete collection')" boxClass="max-w-lg w-11/12" @close="closeDeleteModal">
      <div class="p-4" :class="{ 'text-right': isRtl === true }" :dir="isRtl === true ? 'rtl' : 'ltr'">
        <p class="text-sm text-gray-700 dark:text-gray-200">
          {{ t('entities.deleteCollectionModalMessage', 'Are you sure you want to delete this collection?') }}
        </p>
        <p v-if="collectionPendingDelete !== null" class="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-3">
          {{ collectionPendingDelete.collectionName }}
        </p>
        <div class="entity-collections-delete-actions mt-6" :class="{ 'entity-collections-delete-actions-rtl': isRtl === true }">
          <button type="button" class="btn" :disabled="isDeleting === true" @click="closeDeleteModal">
            {{ t('entities.cancelButton', 'Cancel') }}
          </button>
          <button type="button" class="btn border-transparent bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-900" :disabled="isDeleting === true" @click="deleteCollection">
            <span v-if="isDeleting === true" class="loading loading-spinner loading-xs entity-collections-save-spinner"></span>
            {{ t('entities.deleteConfirmButton', 'Delete') }}
          </button>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import axios from '@/api/axiosInstance';
import { useToast } from 'vue-toastification';
import { globalState } from '@/globalState';
import AppModal from '@/components/AppModal.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import { useI18n } from '@/i18n';

const props = defineProps({
  entities: {
    type: Array,
    default: () => []
  },
  isRtl: {
    type: Boolean,
    default: false
  }
});

const { t, currentLanguage } = useI18n();
const toast = useToast();
const collections = ref([]);
const selectedCollectionId = ref('');
const searchValue = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const collectionModalOpen = ref(false);
const deleteModalOpen = ref(false);
const collectionModalMode = ref('create');
const collectionPendingDelete = ref(null);
const collectionDraft = ref({
  collectionId: '',
  collectionName: '',
  entityIds: []
});

function normalizeStringValue(value) {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value) === true) {
    return String(value);
  }
  return '';
}

function normalizeCollectionId(value) {
  const normalizedValue = normalizeStringValue(value);
  if (normalizedValue === '') {
    return '';
  }
  const parsedValue = Number(normalizedValue);
  if (Number.isFinite(parsedValue) !== true) {
    return '';
  }
  const integerValue = Math.floor(parsedValue);
  if (integerValue < 1) {
    return '';
  }
  return String(integerValue);
}

function normalizeEntityIdList(value) {
  const values = Array.isArray(value) === true ? value : [];
  const normalized = [];
  for (let index = 0; index < values.length; index = index + 1) {
    const entityId = normalizeStringValue(values[index]).substring(0, 255);
    if (entityId === '') {
      continue;
    }
    if (normalized.includes(entityId) === true) {
      continue;
    }
    normalized.push(entityId);
    if (normalized.length >= 500) {
      break;
    }
  }
  return normalized;
}

function getAuthToken() {
  const authTokenRow = document.cookie.split('; ').find(row => row.startsWith('authToken=') !== false);
  const jwtTokenRow = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') !== false);
  const authToken = authTokenRow !== undefined ? authTokenRow.split('=')[1] : '';
  const jwtToken = jwtTokenRow !== undefined ? jwtTokenRow.split('=')[1] : '';
  if (authToken !== '') {
    return authToken;
  }
  return jwtToken;
}

function getAuthHeaders() {
  const token = getAuthToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token !== '') {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

function getLanguageCode() {
  if (currentLanguage.value === 'he') {
    return 'he';
  }
  return 'en';
}

function getLocalizedEntityLabel(localizedMap) {
  if (localizedMap === null || localizedMap === undefined || typeof localizedMap !== 'object' || Array.isArray(localizedMap) === true) {
    return '';
  }
  const languageCode = getLanguageCode();
  if (typeof localizedMap[languageCode] === 'string' && localizedMap[languageCode].trim() !== '') {
    return localizedMap[languageCode].trim();
  }
  if (typeof localizedMap.en === 'string' && localizedMap.en.trim() !== '') {
    return localizedMap.en.trim();
  }
  const keys = Object.keys(localizedMap);
  for (let index = 0; index < keys.length; index = index + 1) {
    const value = localizedMap[keys[index]];
    if (typeof value === 'string' && value.trim() !== '') {
      return value.trim();
    }
  }
  return '';
}

function getEntityDisplayName(entity) {
  if (entity === null || entity === undefined || typeof entity !== 'object') {
    return '';
  }
  const nameLabel = getLocalizedEntityLabel(entity.name);
  if (nameLabel !== '') {
    return nameLabel;
  }
  if (typeof entity.entityType === 'string' && entity.entityType.trim() !== '') {
    return entity.entityType.trim();
  }
  return normalizeStringValue(entity.entityId);
}

function getEntityTypeLabel(entity) {
  if (entity === null || entity === undefined || typeof entity !== 'object') {
    return '';
  }
  const typeLabel = getLocalizedEntityLabel(entity.typeLabels);
  if (typeLabel !== '') {
    return typeLabel;
  }
  return normalizeStringValue(entity.entityType);
}

function normalizeCollectionRow(item) {
  const safeItem = item !== null && item !== undefined && typeof item === 'object' ? item : {};
  const collectionId = normalizeCollectionId(safeItem.collectionId !== undefined ? safeItem.collectionId : safeItem.collectionid);
  if (collectionId === '') {
    return null;
  }
  const collectionName = normalizeStringValue(safeItem.collectionName !== undefined ? safeItem.collectionName : safeItem.collectionname).substring(0, 255);
  return {
    collectionId: collectionId,
    collectionName: collectionName !== '' ? collectionName : collectionId,
    entityIds: normalizeEntityIdList(safeItem.entityIds !== undefined ? safeItem.entityIds : safeItem.entityids),
    createdAt: safeItem.createdAt !== undefined ? safeItem.createdAt : safeItem.createdat,
    updatedAt: safeItem.updatedAt !== undefined ? safeItem.updatedAt : safeItem.updatedat
  };
}

const entityOptions = computed(() => {
  const list = Array.isArray(props.entities) === true ? props.entities : [];
  const options = [];
  for (let index = 0; index < list.length; index = index + 1) {
    const entity = list[index];
    const entityId = normalizeStringValue(entity?.entityId);
    if (entityId === '') {
      continue;
    }
    options.push({
      entityId: entityId,
      displayName: getEntityDisplayName(entity),
      typeLabel: getEntityTypeLabel(entity)
    });
  }
  return options;
});

const filteredCollections = computed(() => {
  const list = Array.isArray(collections.value) === true ? collections.value : [];
  const query = normalizeStringValue(searchValue.value).toLowerCase();
  if (query === '') {
    return list;
  }
  const filtered = [];
  for (let index = 0; index < list.length; index = index + 1) {
    const collection = list[index];
    const name = normalizeStringValue(collection.collectionName).toLowerCase();
    const collectionId = normalizeStringValue(collection.collectionId).toLowerCase();
    if (name.includes(query) === true || collectionId.includes(query) === true) {
      filtered.push(collection);
    }
  }
  return filtered;
});

const selectedCollection = computed(() => {
  if (selectedCollectionId.value === '') {
    return null;
  }
  const list = Array.isArray(collections.value) === true ? collections.value : [];
  for (let index = 0; index < list.length; index = index + 1) {
    if (String(list[index].collectionId) === selectedCollectionId.value) {
      return list[index];
    }
  }
  return null;
});

const collectionModalTitle = computed(() => {
  if (collectionModalMode.value === 'edit') {
    return t('entities.editCollectionModalTitle', 'Edit collection');
  }
  return t('entities.createCollectionModalTitle', 'Create collection');
});

const collectionModalActionLabel = computed(() => {
  if (collectionModalMode.value === 'edit') {
    return t('entities.updateCollectionButton', 'Update');
  }
  return t('entities.saveButton', 'Create');
});

function getCollectionEntityCount(collection) {
  if (collection === null || collection === undefined || Array.isArray(collection.entityIds) !== true) {
    return 0;
  }
  return collection.entityIds.length;
}

function getCollectionCountLabel(collection) {
  return `${getCollectionEntityCount(collection)} ${t('entities.collectionMembersCountLabel', 'entities')}`;
}

function getCollectionMemberRows(collection) {
  if (collection === null || collection === undefined || Array.isArray(collection.entityIds) !== true) {
    return [];
  }
  const rows = [];
  const entities = Array.isArray(props.entities) === true ? props.entities : [];
  for (let index = 0; index < collection.entityIds.length; index = index + 1) {
    const entityId = normalizeStringValue(collection.entityIds[index]);
    if (entityId === '') {
      continue;
    }
    let matchedEntity = null;
    for (let entityIndex = 0; entityIndex < entities.length; entityIndex = entityIndex + 1) {
      if (normalizeStringValue(entities[entityIndex]?.entityId) === entityId) {
        matchedEntity = entities[entityIndex];
        break;
      }
    }
    rows.push({
      entityId: entityId,
      displayName: matchedEntity !== null ? getEntityDisplayName(matchedEntity) : entityId,
      typeLabel: matchedEntity !== null ? getEntityTypeLabel(matchedEntity) : ''
    });
  }
  return rows;
}

function selectCollection(collectionId) {
  selectedCollectionId.value = normalizeCollectionId(collectionId);
}

async function loadCollections() {
  isLoading.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/entitiesAnalysis/entityCollections`, {
      headers: getAuthHeaders()
    });
    const rows = response.data !== undefined && response.data !== null && Array.isArray(response.data.collections) === true ? response.data.collections : [];
    const normalizedCollections = [];
    for (let index = 0; index < rows.length; index = index + 1) {
      const normalizedRow = normalizeCollectionRow(rows[index]);
      if (normalizedRow === null) {
        continue;
      }
      normalizedCollections.push(normalizedRow);
    }
    collections.value = normalizedCollections;
    if (selectedCollectionId.value !== '') {
      let hasSelectedCollection = false;
      for (let index = 0; index < normalizedCollections.length; index = index + 1) {
        if (String(normalizedCollections[index].collectionId) === selectedCollectionId.value) {
          hasSelectedCollection = true;
          break;
        }
      }
      if (hasSelectedCollection !== true) {
        selectedCollectionId.value = '';
      }
    }
    if (selectedCollectionId.value === '' && normalizedCollections.length > 0) {
      selectedCollectionId.value = String(normalizedCollections[0].collectionId);
    }
  } catch (error) {
    collections.value = [];
    toast.error(t('entities.messages.collectionLoadError', 'Failed to load collections.'));
  } finally {
    isLoading.value = false;
  }
}

function openCreateModal() {
  collectionModalMode.value = 'create';
  collectionDraft.value = {
    collectionId: '',
    collectionName: '',
    entityIds: []
  };
  collectionModalOpen.value = true;
}

function openEditModal() {
  if (selectedCollection.value === null) {
    return;
  }
  collectionModalMode.value = 'edit';
  collectionDraft.value = {
    collectionId: String(selectedCollection.value.collectionId),
    collectionName: selectedCollection.value.collectionName,
    entityIds: normalizeEntityIdList(selectedCollection.value.entityIds)
  };
  collectionModalOpen.value = true;
}

function closeCollectionModal() {
  if (isSaving.value === true) {
    return;
  }
  collectionModalOpen.value = false;
}

async function saveCollection() {
  const collectionName = normalizeStringValue(collectionDraft.value.collectionName).substring(0, 255);
  const entityIds = normalizeEntityIdList(collectionDraft.value.entityIds);
  if (collectionName === '') {
    toast.error(t('entities.messages.collectionNameRequired', 'Collection name is required.'));
    return;
  }
  isSaving.value = true;
  try {
    let response = null;
    if (collectionModalMode.value === 'edit') {
      const collectionId = normalizeCollectionId(collectionDraft.value.collectionId);
      if (collectionId === '') {
        toast.error(t('entities.messages.collectionSaveError', 'Failed to save collection.'));
        return;
      }
      response = await axios.put(`${globalState.apiEndpoint}/api/entitiesAnalysis/entityCollections/${encodeURIComponent(collectionId)}`, {
        collectionName: collectionName,
        entityIds: entityIds
      }, {
        headers: getAuthHeaders()
      });
    } else {
      response = await axios.post(`${globalState.apiEndpoint}/api/entitiesAnalysis/entityCollections`, {
        collectionName: collectionName,
        entityIds: entityIds
      }, {
        headers: getAuthHeaders()
      });
    }
    const collection = response.data !== undefined && response.data !== null ? response.data.collection : null;
    collectionModalOpen.value = false;
    await loadCollections();
    if (collection !== null && collection !== undefined) {
      const collectionId = normalizeCollectionId(collection.collectionId);
      if (collectionId !== '') {
        selectedCollectionId.value = collectionId;
      }
    }
    if (collectionModalMode.value === 'edit') {
      toast.success(t('entities.messages.collectionUpdateSuccess', 'Collection updated.'));
    } else {
      toast.success(t('entities.messages.collectionCreateSuccess', 'Collection created.'));
    }
  } catch (error) {
    const responseMessage = typeof error?.response?.data?.message === 'string' ? error.response.data.message.trim() : '';
    if (responseMessage !== '') {
      toast.error(responseMessage);
    } else if (collectionModalMode.value === 'edit') {
      toast.error(t('entities.messages.collectionUpdateError', 'Failed to update collection.'));
    } else {
      toast.error(t('entities.messages.collectionCreateError', 'Failed to create collection.'));
    }
  } finally {
    isSaving.value = false;
  }
}

function openDeleteModal() {
  if (selectedCollection.value === null) {
    return;
  }
  collectionPendingDelete.value = selectedCollection.value;
  deleteModalOpen.value = true;
}

function closeDeleteModal() {
  if (isDeleting.value === true) {
    return;
  }
  deleteModalOpen.value = false;
  collectionPendingDelete.value = null;
}

async function deleteCollection() {
  if (collectionPendingDelete.value === null) {
    return;
  }
  const collectionId = normalizeCollectionId(collectionPendingDelete.value.collectionId);
  if (collectionId === '') {
    return;
  }
  isDeleting.value = true;
  try {
    await axios.delete(`${globalState.apiEndpoint}/api/entitiesAnalysis/entityCollections/${encodeURIComponent(collectionId)}`, {
      headers: getAuthHeaders()
    });
    if (selectedCollectionId.value === collectionId) {
      selectedCollectionId.value = '';
    }
    deleteModalOpen.value = false;
    collectionPendingDelete.value = null;
    await loadCollections();
    toast.success(t('entities.messages.collectionDeleteSuccess', 'Collection deleted.'));
  } catch (error) {
    const responseMessage = typeof error?.response?.data?.message === 'string' ? error.response.data.message.trim() : '';
    if (responseMessage !== '') {
      toast.error(responseMessage);
    } else {
      toast.error(t('entities.messages.collectionDeleteError', 'Failed to delete collection.'));
    }
  } finally {
    isDeleting.value = false;
  }
}

watch(filteredCollections, (list) => {
  if (Array.isArray(list) !== true || list.length === 0) {
    selectedCollectionId.value = '';
    return;
  }
  let exists = false;
  for (let index = 0; index < list.length; index = index + 1) {
    if (String(list[index].collectionId) === selectedCollectionId.value) {
      exists = true;
      break;
    }
  }
  if (exists !== true) {
    selectedCollectionId.value = String(list[0].collectionId);
  }
});

onMounted(async () => {
  await loadCollections();
});

defineExpose({
  openCreateModal,
  reloadCollections: loadCollections
});
</script>

<style scoped>
.entity-collections-page {
  width: 100%;
}

.entity-collections-page-rtl .entity-collections-header-intro {
  text-align: right;
}

.entity-collections-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0.5rem 0.75rem 1rem 0.75rem;
}

.entity-collections-layout {
  display: grid;
  grid-template-columns: minmax(16rem, 22rem) minmax(0, 1fr);
  margin: 0 0.75rem 1.5rem 0.75rem;
}

.entity-collections-list-card,
.entity-collections-detail-card {
  min-height: 24rem;
}

.entity-collections-list-card {
  margin-inline-end: 0.75rem;
  display: flex;
  flex-direction: column;
}

.entity-collections-list-toolbar {
  padding: 1rem 1rem 0.5rem 1rem;
}

.entity-collections-search-input {
  width: 100%;
}

.entity-collections-list-scroll {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 72vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.entity-collections-state {
  min-height: 20rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  color: #6b7280;
  text-align: center;
}

.entity-collections-list {
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
}

.entity-collections-list-item {
  display: flex;
  align-items: flex-start;
  width: 100%;
  text-align: start;
  padding: 0.875rem 1rem;
  margin: 0.25rem 0;
  border: 1px solid rgba(229, 231, 235, 0.9);
  border-radius: 0.75rem;
  background: rgba(249, 250, 251, 0.85);
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.entity-collections-list-item:hover {
  border-color: rgba(99, 102, 241, 0.45);
}

.entity-collections-list-item-active {
  border-color: rgba(79, 70, 229, 0.75);
  background: rgba(238, 242, 255, 0.95);
}

.entity-collections-list-item-icon {
  flex-shrink: 0;
  color: #4f46e5;
  margin-inline-end: 0.75rem;
}

.entity-collections-list-item-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.entity-collections-detail {
  padding: 1.25rem 1.5rem;
}

.entity-collections-detail-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  margin: -0.375rem;
}

.entity-collections-detail-header > * {
  margin: 0.375rem;
}

.entity-collections-detail-icon {
  flex-shrink: 0;
  color: #4f46e5;
}

.entity-collections-detail-heading {
  flex: 1 1 auto;
  min-width: 0;
}

.entity-collections-page-rtl .entity-collections-detail-heading {
  text-align: right;
}

.entity-collections-detail-actions {
  display: flex;
  flex-wrap: wrap;
  flex-shrink: 0;
  margin: -0.25rem;
}

.entity-collections-detail-actions > * {
  margin: 0.25rem;
}

.entity-collections-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  margin: 1.25rem -0.5rem 0 -0.5rem;
}

.entity-collections-detail-field {
  margin: 0.5rem;
}

.entity-collections-detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.entity-collections-detail-value {
  margin-top: 0.35rem;
  font-size: 0.9375rem;
  color: #111827;
}

.dark .entity-collections-detail-value {
  color: #f3f4f6;
}

.entity-collections-detail-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.8125rem;
  word-break: break-all;
}

.entity-collections-members-section {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(229, 231, 235, 0.9);
}

.entity-collections-members-list {
  margin-top: 0.75rem;
}

.entity-collections-member-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(243, 244, 246, 0.9);
}

.entity-collections-member-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.entity-collections-member-id {
  margin-inline-start: 1rem;
  word-break: break-all;
}

.entity-collections-modal-actions,
.entity-collections-delete-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin: -0.25rem;
}

.entity-collections-modal-actions > *,
.entity-collections-delete-actions > * {
  margin: 0.25rem;
}

.entity-collections-modal-actions-rtl,
.entity-collections-delete-actions-rtl {
  justify-content: flex-start;
}

.entity-collections-save-spinner {
  margin-inline-end: 0.5rem;
}

@media (max-width: 900px) {
  .entity-collections-layout {
    grid-template-columns: 1fr;
  }

  .entity-collections-list-card {
    margin-inline-end: 0;
    margin-bottom: 0.75rem;
  }
}
</style>
